import {
  PresentationContent,
  ReferenceSlideImage,
  SlideContent,
  ToolCall,
  ToolResult,
} from "../../types";
import { getCompanyLogos, queryFinster } from "../../finsterApi";
import { rememberLogoUrls } from "./logoCompanyRefs";
import { queryFinsterStream } from "../../api/finster/queries";
import type { FinsterStreamSubEvent } from "../../api/finster/queries";
import type { FinsterQueryDataSource } from "../../finsterApi";
import type { Citation } from "../../citations";
import { exportSlideAsImage } from "../slideService";
import type { PptxLangfuseRunContext } from "../../langfuseTrace";
import { postPptxLangfuseObservation } from "../../langfuseTrace";
import {
  summarizeForToolObservation,
  truncateToolObservationPreview,
} from "../../../shared/services/toolObservationFormat";
import {
  FINSTER_CONTEXT_DISPLAY_TEXT_MAX_LENGTH,
  type PowerPointClientContext,
} from "../../../shared/api/finster/queryTool";
import {
  executeApplyEditsTool,
  executeGetSlidesInfoTool,
  executeInsertLibrarySlideTool,
  executeFetchPreslinkFilesTool,
  getPreslinkTurnBlockReason,
} from "./agentEditTools";
import {
  executeGetLibrarySlidePreviewsTool,
  executeListSlideLibraryCollectionsTool,
  executeSearchSlideLibraryTool,
} from "./slideLibraryAgentTools";
import {
  executePresentDeckPlanTool,
  executeUpdateDeckPlanTool,
  isReviewBlockingDeckBuild,
} from "./deckPlanAgentTools";
import {
  getBrandContext,
  isFactSetChartHelpersEnabled,
  isResearchToolName,
  ToolExecutionPolicy,
  AgentToolContext,
} from "./types";
import {
  activeGraphOperationLabel,
  executeActiveGraphIdLookup,
  executeActiveGraphTool,
  executeListActiveGraphTypes,
  isMutatingActiveGraphOperation,
} from "./activeGraphAgentTools";
import { executeCreatePreslinkChartTool } from "./preslinkChartAgentTools";

const MAX_TOOL_SLIDE_IMAGES = 4;
const MAX_TOTAL_REFERENCE_IMAGES = 5;

export function isToolAllowed(tool: string, policy?: ToolExecutionPolicy): boolean {
  if (tool === "get_slide_images" && policy?.allowSlideImages === false) {
    return false;
  }
  if (tool === "apply_preslink" && policy?.allowPreslink !== true) {
    return false;
  }
  if (tool === "active_graph" && policy?.allowActiveGraph !== true) {
    return false;
  }
  if (tool === "create_preslink_chart" && policy?.allowPreslinkChartCreation !== true) {
    return false;
  }
  if (
    (tool === "list_active_graph_types" || tool === "id_lookup") &&
    !isFactSetChartHelpersEnabled(policy)
  ) {
    return false;
  }
  return true;
}

export function buildBlockedToolResult(tool: string, reason: string): ToolResult {
  return {
    tool,
    success: false,
    error: reason,
  };
}

export interface ExecuteToolsResult {
  results: ToolResult[];
  referenceImages: ReferenceSlideImage[];
  referenceImagesByResultIndex?: Array<ReferenceSlideImage[] | undefined>;
}

interface SingleToolExecutionResult {
  result: ToolResult;
  referenceImages?: ReferenceSlideImage[];
  /** Extra fields merged into the Langfuse tool observation (e.g. FactSet session id). */
  observationMetadata?: Record<string, unknown>;
}

function postToolObservation(
  call: ToolCall,
  result: SingleToolExecutionResult | null,
  options: { langfuseContext?: PptxLangfuseRunContext; startedAtMs: number; error?: unknown }
): void {
  const context = options.langfuseContext;
  if (!context) return;
  const endedAtMs = Date.now();
  // The server proxy owns this TOOL so its actual span id can be forwarded as
  // the W3C parent to data-feed. Posting another browser observation would
  // duplicate the tool node and could not provide trusted parentage.
  if (call.tool === "finster_query" || call.tool === "factset_ai_query") {
    return;
  }

  const errorMessage =
    options.error instanceof Error
      ? options.error.message
      : typeof options.error === "string"
        ? options.error
        : options.error != null
          ? String(options.error)
          : undefined;
  const toolResult = result?.result;
  const success = toolResult ? toolResult.success : false;

  postPptxLangfuseObservation(
    context,
    {
      name: `tool:${call.tool}`,
      input: {
        tool: call.tool,
        argsPreview: formatToolCallArgs(call),
      },
      output: toolResult
        ? {
            success: toolResult.success,
            ...(toolResult.error
              ? { error: truncateToolObservationPreview(toolResult.error) }
              : {}),
            ...(toolResult.data !== undefined
              ? { data: summarizeForToolObservation(toolResult.data) }
              : {}),
            referenceImageCount: result?.referenceImages?.length ?? 0,
          }
        : undefined,
      metadata: {
        tool: call.tool,
        success,
        durationMs: Math.max(0, endedAtMs - options.startedAtMs),
        ...(result?.observationMetadata ?? {}),
      },
      level: success && !errorMessage ? "DEFAULT" : "ERROR",
      statusMessage: errorMessage || toolResult?.error,
      startTime: new Date(options.startedAtMs).toISOString(),
      endTime: new Date(endedAtMs).toISOString(),
    },
    { phase: "multi_hop_executor" }
  );
}

interface SlideNumberMaps {
  slideNumberToIndex: Map<number, number>;
  indexToSlideNumber: Map<number, number>;
}

function buildSlideNumberMapsFromSortedSlides(sortedSlides: SlideContent[]): SlideNumberMaps {
  const slideNumberToIndex = new Map<number, number>();
  const indexToSlideNumber = new Map<number, number>();
  sortedSlides.forEach((slide) => {
    const slideNumber = slide.slideNumber ?? Math.max(1, slide.index + 1);
    slideNumberToIndex.set(slideNumber, slide.index);
    indexToSlideNumber.set(slide.index, slideNumber);
  });
  return { slideNumberToIndex, indexToSlideNumber };
}

export interface ResolvedSlideImageRequest {
  requestedSlideNumbers: number[];
  resolvedSlideNumbers: number[];
  resolvedSlideIndices: number[];
  skippedSlideNumbers: number[];
  truncatedByMax: boolean;
  maxImages: number;
}

export function resolveSlideImageRequest(
  presentationContent: PresentationContent,
  requestedSlideNumbers: number[] | undefined,
  requestedMaxImages?: number,
  defaultMaxImages: number = MAX_TOOL_SLIDE_IMAGES
): ResolvedSlideImageRequest {
  const normalizedRequested = (requestedSlideNumbers || [])
    .map((value) => Math.floor(value))
    .filter((value) => Number.isFinite(value) && value >= 0);

  const dedupedRequested: number[] = [];
  const seenRequested = new Set<number>();
  for (const slideNumber of normalizedRequested) {
    if (!seenRequested.has(slideNumber)) {
      seenRequested.add(slideNumber);
      dedupedRequested.push(slideNumber);
    }
  }

  const normalizedMaxImages =
    typeof requestedMaxImages === "number" &&
    Number.isFinite(requestedMaxImages) &&
    requestedMaxImages > 0
      ? Math.floor(requestedMaxImages)
      : defaultMaxImages;
  const maxImages = Math.max(1, Math.min(normalizedMaxImages, defaultMaxImages));

  const sortedSlides = [...presentationContent.slides].sort((a, b) => a.index - b.index);
  const { slideNumberToIndex } = buildSlideNumberMapsFromSortedSlides(sortedSlides);

  const resolvedSlideNumbers: number[] = [];
  const resolvedSlideIndices: number[] = [];
  const skippedSlideNumbers: number[] = [];
  let truncatedByMax = false;

  for (const slideNumber of dedupedRequested) {
    const slideIndex = slideNumberToIndex.get(slideNumber);
    if (slideIndex === undefined) {
      skippedSlideNumbers.push(slideNumber);
      continue;
    }
    if (resolvedSlideNumbers.length >= maxImages) {
      truncatedByMax = true;
      skippedSlideNumbers.push(slideNumber);
      continue;
    }
    resolvedSlideNumbers.push(slideNumber);
    resolvedSlideIndices.push(slideIndex);
  }

  return {
    requestedSlideNumbers: dedupedRequested,
    resolvedSlideNumbers,
    resolvedSlideIndices,
    skippedSlideNumbers,
    truncatedByMax,
    maxImages,
  };
}

export function mergeReferenceImages(
  initialImages: string[] | undefined,
  toolImages: string[] | undefined,
  maxTotalImages: number = MAX_TOTAL_REFERENCE_IMAGES
): string[] | undefined {
  const maxImages = Math.max(1, maxTotalImages);
  const merged: string[] = [];
  const seen = new Set<string>();

  const appendImages = (images?: string[]) => {
    if (!images) return;
    for (const image of images) {
      if (!image || seen.has(image)) continue;
      if (merged.length >= maxImages) return;
      seen.add(image);
      merged.push(image);
    }
  };

  appendImages(initialImages);
  appendImages(toolImages);

  return merged.length > 0 ? merged : undefined;
}

export function formatToolCallArgs(call: ToolCall): string {
  if (call.tool === "get_slide_images") {
    const slideNumbers =
      call.slide_numbers && call.slide_numbers.length > 0 ? call.slide_numbers.join(",") : "(none)";
    return `slide_numbers=${slideNumbers}${call.max_images ? ` max_images=${call.max_images}` : ""}`;
  }
  if (isResearchToolName(call.tool)) {
    return `${call.query || ""}${call.queryMode ? ` [${call.queryMode}]` : ""}`;
  }
  if (call.tool === "apply_preslink") {
    const mode =
      call.preslinkOperation ?? (isMutatingPreslinkCall(call) ? "refresh" : "inspect");
    return `${mode} scope=${call.preslinkScope ?? "shapes"} shapes=${
      call.preslinkShapeRefs?.length ?? 0
    }`;
  }
  if (call.tool === "get_company_logo") {
    const names =
      call.company_names && call.company_names.length > 0
        ? call.company_names
        : call.company_name
          ? [call.company_name]
          : [];
    return names.length > 0 ? names.join(", ") : "";
  }
  if (call.tool === "active_graph") {
    return activeGraphOperationLabel(call.activeGraphInput);
  }
  if (call.tool === "id_lookup") {
    return call.idLookupSearchText ?? "";
  }
  if (call.tool === "list_active_graph_types") {
    return "enabled PowerPoint live-chart types";
  }
  return call.query || "";
}

function normalizeContextValue(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const normalized = value.trim();
  return normalized || undefined;
}

function derivePresentationName(
  presentationRef: AgentToolContext["presentationRef"]
): string | undefined {
  const name = normalizeContextValue(presentationRef?.name);
  const fullName = normalizeContextValue(presentationRef?.fullName);
  const basename = fullName
    ? normalizeContextValue(fullName.split(/[\\/]/).filter(Boolean).pop())
    : undefined;
  if (basename) return basename;
  if (name) return name;
  return undefined;
}

function normalizeSlideTitle(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const normalized = value.replace(/\s+/g, " ").trim();
  if (!normalized) return undefined;
  return Array.from(normalized)
    .slice(0, FINSTER_CONTEXT_DISPLAY_TEXT_MAX_LENGTH)
    .join("")
    .trim();
}

function isTitlePlaceholder(placeholderType: string | undefined): boolean {
  const semanticType = (placeholderType || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "")
    .replace(/placeholder/g, "");
  return semanticType === "title" || semanticType === "centertitle";
}

function shapeNameIndicatesTitle(name: string): boolean {
  const words = name
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
  return words.includes("title") && !words.includes("subtitle");
}

function extractSlideTitle(slide: SlideContent): string | undefined {
  const placeholderTitle = slide.shapes.find(
    (shape) =>
      isTitlePlaceholder(shape.placeholderType) &&
      Boolean(normalizeSlideTitle(shape.text))
  );
  if (placeholderTitle) return normalizeSlideTitle(placeholderTitle.text);

  const namedTitle = slide.shapes.find(
    (shape) =>
      shapeNameIndicatesTitle(shape.name) && Boolean(normalizeSlideTitle(shape.text))
  );
  return namedTitle ? normalizeSlideTitle(namedTitle.text) : undefined;
}

export function buildPowerPointClientContext(
  presentationRef: AgentToolContext["presentationRef"],
  presentationContent?: PresentationContent
): PowerPointClientContext {
  const presentationId = normalizeContextValue(
    presentationRef?.openPresentationId
  );
  const presentationName = derivePresentationName(presentationRef);
  const selectedSlide = presentationContent
    ? presentationContent.slides.find(
        (slide) => slide.index === presentationContent.selectedSlideIndex
      ) || presentationContent.slides[presentationContent.selectedSlideIndex]
    : undefined;
  const slideId = normalizeContextValue(selectedSlide?.id);
  const slideNumber = selectedSlide
    ? selectedSlide.slideNumber ?? selectedSlide.index + 1
    : undefined;
  const slideTitle = selectedSlide
    ? extractSlideTitle(selectedSlide)
    : undefined;

  return {
    ...(presentationId ? { presentation_id: presentationId } : {}),
    ...(presentationName ? { presentation_name: presentationName } : {}),
    ...(slideId ? { slide_id: slideId } : {}),
    ...(typeof slideNumber === "number" &&
    Number.isInteger(slideNumber) &&
    slideNumber >= 0
      ? { slide_number: slideNumber }
      : {}),
    ...(slideTitle ? { slide_title: slideTitle } : {}),
  };
}

function isMutatingPreslinkCall(call: ToolCall): boolean {
  const rawCall = call as ToolCall & {
    operation?: unknown;
    process_presentation_file?: unknown;
  };
  return (
    call.tool === "apply_preslink" &&
    (call.preslinkOperation === "refresh" ||
      rawCall.operation === "refresh" ||
      call.processPresentationFile === true ||
      rawCall.process_presentation_file === true)
  );
}

function isMutatingActiveGraphCall(call: ToolCall): boolean {
  return (
    call.tool === "active_graph" &&
    isMutatingActiveGraphOperation(call.activeGraphInput)
  );
}

function markPreslinkMutationFailure(ctx: AgentToolContext | undefined, error: unknown): void {
  if (!ctx) return;
  ctx.preslinkMutationFailedReason =
    error instanceof Error ? error.message : String(error || "PresLink refresh failed.");
}

async function executeSingleTool(
  call: ToolCall,
  options: {
    presentationContent?: PresentationContent;
    finsterDataSources?: readonly FinsterQueryDataSource[];
    toolPolicy?: ToolExecutionPolicy;
    agentToolContext?: AgentToolContext;
    langfuseContext?: PptxLangfuseRunContext;
    goldenSubdirectory?: string;
    onSubEvent?: (event: FinsterStreamSubEvent) => void;
    onToolProgress?: (message: string) => void;
  } = {}
): Promise<SingleToolExecutionResult> {
  const { presentationContent, finsterDataSources, toolPolicy } = options;
  const startedAtMs = Date.now();
  try {
    if (!isToolAllowed(call.tool, toolPolicy)) {
      return {
        result: buildBlockedToolResult(
          call.tool,
          `Tool ${call.tool} is disabled for this request context.`,
        ),
      };
    }
    if (call.tool === "insert_library_slide") {
      const preslinkBlockReason = getPreslinkTurnBlockReason(options.agentToolContext);
      if (preslinkBlockReason) {
        return {
          result: buildBlockedToolResult(call.tool, preslinkBlockReason),
        };
      }
    }
    if (
      isReviewBlockingDeckBuild(options.agentToolContext) &&
      (call.tool === "insert_library_slide" ||
        call.tool === "apply_edits" ||
        call.tool === "create_preslink_chart" ||
        (call.tool === "active_graph" &&
          isMutatingActiveGraphOperation(call.activeGraphInput)))
    ) {
      const isBuildMutation =
        call.tool === "insert_library_slide" ||
        call.tool === "create_preslink_chart" ||
        (call.tool === "active_graph" &&
          isMutatingActiveGraphOperation(call.activeGraphInput)) ||
        Boolean(
          call.modifications?.slides?.some(
            (slide) => slide.action === "insert" || slide.action === "duplicate"
          )
        );
      if (isBuildMutation) {
        return {
          result: buildBlockedToolResult(
            call.tool,
            "A deck plan is open in Review. Use update_deck_plan until the user confirms Create slides."
          ),
        };
      }
    }
    let singleResult: SingleToolExecutionResult;
    switch (call.tool) {
      case "get_company_logo": {
        const companyNames =
          call.company_names && call.company_names.length > 0
            ? call.company_names
            : call.company_name
              ? [call.company_name]
              : [];
        const logoResult = await getCompanyLogos(companyNames, call.size);
        rememberLogoUrls(options.agentToolContext, logoResult.results);
        const toolSuccess = logoResult.success && logoResult.resolvedCount > 0;
        singleResult = {
          result: {
            tool: "get_company_logo",
            success: toolSuccess,
            data: {
              results: logoResult.results,
              resolvedCount: logoResult.resolvedCount,
              failedCount: logoResult.failedCount,
              failedCompanies: logoResult.failedCompanies,
              debugLog: logoResult.debugLog,
            },
            error: toolSuccess
              ? logoResult.error
              : logoResult.error || "No company logos were found.",
          },
        };
        break;
      }
      case "finster_query":
      case "factset_ai_query": {
        const normalizedQuery = typeof call.query === "string" ? call.query.trim() : "";
        const brand = getBrandContext(toolPolicy);
        const publicToolName = brand.researchToolName;
        if (!normalizedQuery) {
          singleResult = {
            result: {
              tool: publicToolName,
              success: false,
              error: `Tool ${publicToolName} requires a non-empty "query" string.`,
            },
          };
          break;
        }
        if (
          toolPolicy?.finsterDataSourceScopeIsAuthoritative === true &&
          finsterDataSources?.length === 0 &&
          (toolPolicy.userFileIds?.length ?? 0) === 0
        ) {
          singleResult = {
            result: {
              tool: publicToolName,
              success: false,
              error: "No research data sources are available in the resolved Skill scope.",
            },
          };
          break;
        }
        const queryMode = call.queryMode ?? "thinking";
        console.log(`[TOOL] ${publicToolName} (${queryMode}): "${normalizedQuery}"`);
        const clientContext = buildPowerPointClientContext(
          options.agentToolContext?.presentationRef,
          presentationContent
        );
        const researchScope = {
          tool: publicToolName,
          query: normalizedQuery,
          finsterDataSources,
          userFileIds: toolPolicy?.userFileIds,
        } as const;
        const queryResult = options.onSubEvent
          ? await queryFinsterStream(
              normalizedQuery,
              finsterDataSources,
              brand.publicBrandName,
              queryMode,
              options.onSubEvent,
              options.agentToolContext?.signal,
              toolPolicy?.userFileIds,
              options.langfuseContext,
              clientContext,
              options.langfuseContext?.sessionId,
              options.agentToolContext?.selectedSkillsGuidance,
              ...(toolPolicy?.finsterDataSourceScopeIsAuthoritative === true
                ? [true] as const
                : [])
            )
          : await queryFinster(
              normalizedQuery,
              finsterDataSources,
              brand.publicBrandName,
              queryMode,
              toolPolicy?.userFileIds,
              options.langfuseContext,
              clientContext,
              options.langfuseContext?.sessionId,
              options.agentToolContext?.selectedSkillsGuidance,
              ...(toolPolicy?.finsterDataSourceScopeIsAuthoritative === true
                ? [true] as const
                : [])
            );
        if (
          queryResult.success &&
          queryResult.answer &&
          options.agentToolContext?.onResearchResult
        ) {
          await options.agentToolContext.onResearchResult({
            ...researchScope,
            answer: queryResult.answer,
            ...(Array.isArray(queryResult.citations)
              ? { citations: queryResult.citations as Citation[] }
              : undefined),
            ...(queryResult.sessionId ? { sessionId: queryResult.sessionId } : undefined),
          });
        }
        const factsetObservationMetadata: Record<string, unknown> = {
          ...(queryResult.sessionId ? { factsetSessionId: queryResult.sessionId } : {}),
          ...(queryResult.sessionName ? { factsetSessionName: queryResult.sessionName } : {}),
        };
        singleResult = {
          result: {
            tool: publicToolName,
            success: queryResult.success,
            data: queryResult.answer
              ? { answer: queryResult.answer, citations: queryResult.citations }
              : undefined,
            error: queryResult.error,
          },
          ...(Object.keys(factsetObservationMetadata).length > 0
            ? { observationMetadata: factsetObservationMetadata }
            : {}),
        };
        break;
      }
      case "get_slide_images": {
        if (!presentationContent) {
          singleResult = {
            result: {
              tool: "get_slide_images",
              success: false,
              error: "Slide image tool unavailable without presentation context.",
            },
          };
          break;
        }

        const resolution = resolveSlideImageRequest(
          presentationContent,
          call.slide_numbers,
          call.max_images
        );

        if (resolution.requestedSlideNumbers.length === 0) {
          singleResult = {
            result: {
              tool: "get_slide_images",
              success: false,
              error: "No valid slide_numbers were provided for get_slide_images.",
              data: {
                requestedSlideNumbers: [],
                capturedSlideNumbers: [],
                skippedSlideNumbers: [],
                maxImages: resolution.maxImages,
                truncatedByMax: false,
              },
            },
          };
          break;
        }

        const keyedReferenceImages: ReferenceSlideImage[] = [];
        const capturedSlideNumbers: number[] = [];
        const captureFailedSlideNumbers: number[] = [];
        const slideNumberToId = new Map(
          presentationContent.slides.map((slide) => [
            slide.slideNumber ?? Math.max(1, slide.index + 1),
            slide.id,
          ])
        );

        for (let idx = 0; idx < resolution.resolvedSlideNumbers.length; idx++) {
          const slideNumber = resolution.resolvedSlideNumbers[idx];
          const slideIndex = resolution.resolvedSlideIndices[idx];
          try {
            const base64 = await exportSlideAsImage(slideIndex);
            keyedReferenceImages.push({
              slideId: slideNumberToId.get(slideNumber) ?? `slideNumber:${slideNumber}`,
              image: base64,
            });
            capturedSlideNumbers.push(slideNumber);
          } catch (error) {
            const errMsg = error instanceof Error ? error.message : String(error);
            console.warn(
              `[TOOL] get_slide_images failed for slide ${slideNumber} (index ${slideIndex}): ${errMsg}`
            );
            captureFailedSlideNumbers.push(slideNumber);
          }
        }

        const skippedSlideNumbers = Array.from(
          new Set([...resolution.skippedSlideNumbers, ...captureFailedSlideNumbers])
        ).sort((a, b) => a - b);

        const success = keyedReferenceImages.length > 0;
        console.log(
          `[TOOL] get_slide_images requested=${resolution.requestedSlideNumbers.join(",")} ` +
            `captured=${capturedSlideNumbers.join(",") || "(none)"} ` +
            `skipped=${skippedSlideNumbers.join(",") || "(none)"}`
        );

        singleResult = {
          result: {
            tool: "get_slide_images",
            success,
            data: {
              requestedSlideNumbers: resolution.requestedSlideNumbers,
              capturedSlideNumbers,
              skippedSlideNumbers,
              maxImages: resolution.maxImages,
              truncatedByMax: resolution.truncatedByMax,
              capturedCount: keyedReferenceImages.length,
            },
            error: success ? undefined : "No slide images could be captured.",
          },
          referenceImages: keyedReferenceImages,
        };
        break;
      }
      case "active_graph": {
        if (!call.activeGraphInput) {
          singleResult = {
            result: {
              tool: "active_graph",
              success: false,
              error: "active_graph requires an operation payload.",
            },
          };
          break;
        }
        const activeGraphResult = await executeActiveGraphTool(
          call.activeGraphInput,
          {
            presentationContent,
            policy: toolPolicy,
            context: options.agentToolContext,
          },
        );
        singleResult = {
          result: {
            tool: "active_graph",
            success: activeGraphResult.ok,
            data: activeGraphResult,
            error: activeGraphResult.ok
              ? undefined
              : activeGraphResult.message,
          },
        };
        break;
      }
      case "create_preslink_chart": {
        if (!call.preslinkChartInput?.chartParameters) {
          singleResult = {
            result: {
              tool: "create_preslink_chart",
              success: false,
              error: "create_preslink_chart requires chartParameters.",
            },
          };
          break;
        }
        const preslinkChartResult = await executeCreatePreslinkChartTool(
          call.preslinkChartInput,
          options.agentToolContext ?? {},
        );
        singleResult = {
          result: {
            tool: "create_preslink_chart",
            success: preslinkChartResult.ok,
            data: preslinkChartResult,
            error: preslinkChartResult.ok ? undefined : preslinkChartResult.message,
          },
        };
        break;
      }
      case "list_active_graph_types": {
        const activeGraphTypes = executeListActiveGraphTypes(toolPolicy);
        singleResult = {
          result: {
            tool: "list_active_graph_types",
            success: activeGraphTypes.ok === true,
            data: activeGraphTypes,
            error:
              activeGraphTypes.ok === true
                ? undefined
                : "Live FactSet ActiveGraphs are disabled for this request context.",
          },
        };
        break;
      }
      case "id_lookup": {
        const lookupResult = call.idLookupSearchText
          ? await executeActiveGraphIdLookup(
              { searchText: call.idLookupSearchText },
              options.agentToolContext,
              toolPolicy,
            )
          : null;
        singleResult = {
          result: {
            tool: "id_lookup",
            success: lookupResult?.success === true,
            data: lookupResult ?? undefined,
            error:
              lookupResult?.success === true
                ? undefined
                : lookupResult?.error ??
                  (call.idLookupSearchText
                    ? "FactSet company lookup did not return a usable match."
                    : "id_lookup requires search_text."),
          },
        };
        break;
      }
      case "apply_edits":
        singleResult = await executeApplyEditsTool(call, {
          ...options,
          onProgress: options.onToolProgress,
        });
        break;
      case "get_slides_info":
        singleResult = await executeGetSlidesInfoTool(call, options);
        break;
      case "insert_library_slide":
        singleResult = await executeInsertLibrarySlideTool(call, options);
        break;
      case "list_slide_library_collections":
        singleResult = await executeListSlideLibraryCollectionsTool(options);
        break;
      case "search_slide_library":
        singleResult = await executeSearchSlideLibraryTool(call, options);
        break;
      case "get_library_slide_previews":
        singleResult = await executeGetLibrarySlidePreviewsTool(call, options);
        break;
      case "present_deck_plan":
        singleResult = await executePresentDeckPlanTool(call, options);
        break;
      case "update_deck_plan":
        singleResult = await executeUpdateDeckPlanTool(call, options);
        break;
      case "apply_preslink":
        singleResult = await executeFetchPreslinkFilesTool(call, {
          ...options,
          onProgress: options.onToolProgress,
        });
        break;
      default:
        singleResult = {
          result: { tool: call.tool, success: false, error: `Unknown tool: ${call.tool}` },
        };
    }
    if (isMutatingPreslinkCall(call) && !singleResult.result.success) {
      markPreslinkMutationFailure(
        options.agentToolContext,
        singleResult.result.error || "PresLink refresh failed."
      );
    }

    postToolObservation(call, singleResult, {
      langfuseContext: options.langfuseContext,
      startedAtMs,
    });
    return singleResult;
  } catch (error) {
    if (isMutatingPreslinkCall(call)) {
      markPreslinkMutationFailure(options.agentToolContext, error);
    }
    postToolObservation(call, null, {
      langfuseContext: options.langfuseContext,
      startedAtMs,
      error,
    });
    throw error;
  }
}

export async function executeTools(
  toolCalls: ToolCall[],
  options: {
    presentationContent?: PresentationContent;
    finsterDataSources?: readonly FinsterQueryDataSource[];
    toolPolicy?: ToolExecutionPolicy;
    agentToolContext?: AgentToolContext;
    langfuseContext?: PptxLangfuseRunContext;
    goldenSubdirectories?: Array<string | undefined>;
    onToolEvent?: (toolCallIndex: number, event: FinsterStreamSubEvent) => void;
    onToolProgress?: (toolCallIndex: number, message: string) => void;
  } = {}
): Promise<ExecuteToolsResult> {
  // Mutability comes from the explicit tool argument, while policy is checked
  // independently by the executor. Read-only PresLink inspection may remain
  // concurrent with observations.
  const hasMutatingPreslink = toolCalls.some(isMutatingPreslinkCall);
  const hasMutatingActiveGraph = toolCalls.some(isMutatingActiveGraphCall);
  const hasPreslinkChartCreate = toolCalls.some((call) => call.tool === "create_preslink_chart");
  const hasPlanningResearch = toolCalls.some((call) =>
    isResearchToolName(call.tool)
  );
  const shouldSerializePlanningEvidence =
    hasPlanningResearch &&
    Boolean(options.agentToolContext?.onResearchResult);
  const MAX_CONCURRENT = toolCalls.some((call) => {
    if (
      call.tool === "apply_edits" ||
      call.tool === "insert_library_slide" ||
      call.tool === "create_preslink_chart"
    ) {
      return true;
    }
    return isMutatingPreslinkCall(call) || isMutatingActiveGraphCall(call);
  }) || shouldSerializePlanningEvidence
    ? 1
    : 2;
  const results: ToolResult[] = new Array(toolCalls.length);
  const referenceImagesByResultIndex: Array<ReferenceSlideImage[] | undefined> = new Array(
    toolCalls.length
  );
  const referenceImages: ReferenceSlideImage[] = [];
  const indexedToolCalls = toolCalls.map((call, originalIndex) => ({
    call,
    originalIndex,
  }));
  const hasPlanCommit = toolCalls.some(
    (call) =>
      call.tool === "present_deck_plan" ||
      call.tool === "update_deck_plan"
  );
  let executionEntries = indexedToolCalls;
  if (shouldSerializePlanningEvidence && hasPlanCommit) {
    const researchEntries = indexedToolCalls.filter((entry) =>
      isResearchToolName(entry.call.tool)
    );
    const otherEntries = indexedToolCalls.filter(
      (entry) => !isResearchToolName(entry.call.tool)
    );
    const firstPlanIndex = indexedToolCalls.find(
      (entry) =>
        entry.call.tool === "present_deck_plan" ||
        entry.call.tool === "update_deck_plan"
    )!.originalIndex;
    const insertionIndex = otherEntries.findIndex(
      (entry) =>
        entry.originalIndex >=
        Math.min(firstPlanIndex, researchEntries[0].originalIndex)
    );
    executionEntries =
      insertionIndex < 0
        ? [...otherEntries, ...researchEntries]
        : [
            ...otherEntries.slice(0, insertionIndex),
            ...researchEntries,
            ...otherEntries.slice(insertionIndex),
          ];
  }

  for (let start = 0; start < executionEntries.length; start += MAX_CONCURRENT) {
    const batch = executionEntries.slice(start, start + MAX_CONCURRENT);
    const executeBatchCall = (entry: (typeof indexedToolCalls)[number]) => {
      const { call, originalIndex } = entry;
      // A refresh replaces slides, so ids authored in the same hop are already
      // stale by the time they would apply. Inspect-only calls leave ids intact
      // and stay concurrent.
      if (
        hasMutatingPreslink &&
        (call.tool === "apply_edits" ||
          call.tool === "insert_library_slide" ||
          call.tool === "create_preslink_chart" ||
          isMutatingActiveGraphCall(call))
      ) {
        const deferred: SingleToolExecutionResult = {
          result: buildBlockedToolResult(
            call.tool,
            `Tool ${call.tool} was deferred because the same model hop started a PresLink refresh, which replaces slides and invalidates these ids. Observe the refreshed ids, then call ${call.tool} in a later hop.`
          ),
        };
        return Promise.resolve({
          index: originalIndex,
          status: "fulfilled" as const,
          value: deferred,
        });
      }
      if (
        hasMutatingActiveGraph &&
        (call.tool === "apply_edits" || call.tool === "insert_library_slide")
      ) {
        const deferred: SingleToolExecutionResult = {
          result: buildBlockedToolResult(
            call.tool,
            `Tool ${call.tool} was deferred because the same model hop mutates a live FactSet chart. Observe the chart result, then run other presentation mutations in a later hop.`
          ),
        };
        return Promise.resolve({
          index: originalIndex,
          status: "fulfilled" as const,
          value: deferred,
        });
      }
      if (
        hasPreslinkChartCreate &&
        (call.tool === "apply_edits" || call.tool === "insert_library_slide")
      ) {
        const deferred: SingleToolExecutionResult = {
          result: buildBlockedToolResult(
            call.tool,
            `Tool ${call.tool} was deferred because the same model hop creates a linked FactSet chart. Observe the chart result, then run other presentation mutations in a later hop.`
          ),
        };
        return Promise.resolve({
          index: originalIndex,
          status: "fulfilled" as const,
          value: deferred,
        });
      }
      return executeSingleTool(call, {
        ...options,
        presentationContent:
          options.agentToolContext?.latestPresentationContent ?? options.presentationContent,
        goldenSubdirectory: options.goldenSubdirectories?.[originalIndex],
        onSubEvent: options.onToolEvent
          ? (event) => options.onToolEvent!(originalIndex, event)
          : undefined,
        onToolProgress: options.onToolProgress
          ? (message) => options.onToolProgress!(originalIndex, message)
          : undefined,
      }).then(
        (value) => ({ index: originalIndex, status: "fulfilled" as const, value }),
        (reason) => ({ index: originalIndex, status: "rejected" as const, reason })
      );
    };
    // If the model emits research and present/update in one hop, finish the
    // research (including its evidence commit) before the plan callback. This
    // preserves model result ordering while preventing Review confirmation
    // from racing ahead of the evidence pack.
    const researchPromises = batch.map((entry) =>
      shouldSerializePlanningEvidence &&
      isResearchToolName(entry.call.tool)
        ? executeBatchCall(entry)
        : null
    );
    const researchBarrier = Promise.all(
      researchPromises.filter(
        (promise): promise is Exclude<(typeof researchPromises)[number], null> => promise !== null
      )
    );
    const batchPromises = batch.map((entry, idx) => {
      const researchPromise = researchPromises[idx];
      if (researchPromise) return researchPromise;
      if (
        shouldSerializePlanningEvidence &&
        (entry.call.tool === "present_deck_plan" ||
          entry.call.tool === "update_deck_plan")
      ) {
        return researchBarrier.then(() => executeBatchCall(entry));
      }
      return executeBatchCall(entry);
    });
    const settled = await Promise.all(batchPromises);
    for (const result of settled) {
      if (result.status === "fulfilled") {
        results[result.index] = result.value.result;
        if (result.value.referenceImages && result.value.referenceImages.length > 0) {
          referenceImagesByResultIndex[result.index] = result.value.referenceImages;
          referenceImages.push(...result.value.referenceImages);
        }
      } else {
        const tool = isResearchToolName(toolCalls[result.index].tool)
          ? getBrandContext(options.toolPolicy).researchToolName
          : toolCalls[result.index].tool;
        results[result.index] = {
          tool,
          success: false,
          error: result.reason?.message || "Tool execution failed",
        };
      }
    }
  }

  return { results, referenceImages, referenceImagesByResultIndex };
}
